// @owners { team: patient-health }
import { Body, Button } from '@alto/design-system';
import { usePrevious } from '@alto/design-system/src/hooks/usePrevious';
import { CashPayInsurance, EmptyInsurance } from '@alto/features';
// eslint-disable-next-line @alto/no-pocky-import
import {
  DeprecatedModalBody,
  DeprecatedModalFooter,
  FlexColumn,
  FlexRow,
  Link,
  Loader,
  SectionContainer,
  SectionContainerSection,
  spacing,
} from '@alto/pocky';
import React, { useEffect, useState } from 'react';
import { type ModalType } from '~shared/actions/modal';
import { type ResolutionValues } from '~shared/lib/analytics/src/events/onboarding/getInsuranceCompleted';
import { type Insurance as InsuranceType } from '~shared/types/clients';
import { Insurance } from '~web/features/insurances/components/Insurance';
import InsuranceForm from '~web/features/insurances/components/InsuranceFormContainer';
import InsuranceList from '~web/features/insurances/components/InsuranceList';
import InsurancePhotoForm from '~web/features/insurances/components/InsurancePhotoFormContainer';
import cardFinder from '~web/images/insuranceCard@1x.png';

export type Props = {
  readonly fetchInsurances: () => void;
  readonly next: () => void;
  readonly updateCashPay: () => void;
  readonly insurances: InsuranceType[];
  readonly cardFinderInsurances: InsuranceType[];
  readonly isNeedsInsurance: boolean;
  readonly isCashPayOnly: boolean;
  readonly loading: boolean;
  readonly success: boolean | null;
  readonly openModal: (modalType: ModalType) => void;
  readonly trackInsuranceStepCompleted: (cardFinderInsurancePresent: boolean, resolution: ResolutionValues) => void;
  readonly resolution: ResolutionValues;
};

export const OnboardingInsuranceForm = ({
  fetchInsurances,
  next,
  updateCashPay,
  insurances,
  cardFinderInsurances,
  isNeedsInsurance,
  isCashPayOnly,
  loading,
  success,
  openModal,
  trackInsuranceStepCompleted,
  resolution,
}: Props) => {
  const [isAddingInsurance, setIsAddingInsurance] = useState(false);
  const [isUploadingInsurance, setIsUploadingInsurance] = useState(false);
  const prevSuccess = usePrevious(success);

  const handleCloseAll = () => {
    setIsAddingInsurance(false);
    setIsUploadingInsurance(false);
  };

  useEffect(() => {
    fetchInsurances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!prevSuccess && success) {
      handleCloseAll();
    }
  }, [prevSuccess, success]);

  const handleAddInsurance = () => {
    setIsAddingInsurance(true);
    setIsUploadingInsurance(false);
  };

  const handleUploadInsurance = () => {
    setIsAddingInsurance(false);
    setIsUploadingInsurance(true);
  };

  const handleNext = () => {
    trackInsuranceStepCompleted(cardFinderInsurances.length > 0, resolution);
    next();
  };

  const renderBody = () => {
    if (loading) {
      return <Loader text="Loading your insurances..." />;
    }

    if (isNeedsInsurance) {
      return (
        <EmptyInsurance
          onSkip={handleNext}
          onAddInsurance={handleAddInsurance}
          onSelectCashPay={updateCashPay}
        />
      );
    }

    if (isCashPayOnly) {
      return (
        <CashPayInsurance
          onAddInsurance={handleAddInsurance}
          source="onboarding"
        />
      );
    }

    let insuranceContent = (
      <InsuranceList
        insurances={insurances}
        source="onboarding"
      />
    );

    const firstCardFinderInsurance = cardFinderInsurances[0];

    if (firstCardFinderInsurance) {
      insuranceContent = (
        <FlexRow spacing={spacing.smSpacing}>
          <SectionContainer>
            <SectionContainerSection>
              <FlexColumn
                verticallyAlignContent
                evenlySpaceContent
              >
                <FlexRow>
                  <Body>We found your insurance!</Body>
                  <Link
                    onClick={() => {
                      openModal('CARDFINDER_INFO_MODAL');
                    }}
                  >
                    How does it work?
                  </Link>
                </FlexRow>
                <img
                  width="44px"
                  height="44px"
                  alt="cardfinder"
                  src={cardFinder}
                />
              </FlexColumn>
            </SectionContainerSection>
          </SectionContainer>
          <SectionContainer>
            <SectionContainerSection>
              <Insurance
                hideActions
                insurance={firstCardFinderInsurance}
                source="onboarding"
              />
            </SectionContainerSection>
          </SectionContainer>
        </FlexRow>
      );
    }

    return (
      <FlexRow spacing={spacing.mdSpacing}>
        <Body>
          Have additional insurance or a prescription savings card? Add them all, so we can get you the best pricing.
        </Body>
        {insuranceContent}
      </FlexRow>
    );
  };

  const hasInsurances = insurances.length !== 0;

  if (isAddingInsurance) {
    return (
      <InsuranceForm
        onCancel={handleCloseAll}
        showPhotoForm={handleUploadInsurance}
      />
    );
  }

  if (isUploadingInsurance) {
    return <InsurancePhotoForm onCancel={handleCloseAll} />;
  }

  return (
    <>
      <DeprecatedModalBody>{renderBody()}</DeprecatedModalBody>
      {hasInsurances || isCashPayOnly ? (
        <DeprecatedModalFooter>
          <Button
            type={isCashPayOnly ? 'primary' : 'tertiary'}
            small
            onPress={handleNext}
          >
            Next
          </Button>
          <Button
            type="primary"
            small
            onPress={handleAddInsurance}
          >
            Add another benefit
          </Button>
        </DeprecatedModalFooter>
      ) : null}
    </>
  );
};
